import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  InboxIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const mainNavItems: NavItem[] = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Inbox', href: '/inbox', icon: InboxIcon },
  { name: 'My Work', href: '/my-work', icon: Squares2X2Icon },
];

const secondaryNavItems: NavItem[] = [
  { name: 'Calendar', href: '/calendar', icon: CalendarIcon },
  { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

// Mock workspaces - will be replaced with actual data from API
const mockWorkspaces = [
  { id: '1', name: 'Engineering', boards: ['Sprint Planning', 'Bug Tracker', 'Roadmap'] },
  { id: '2', name: 'Marketing', boards: ['Campaign Calendar', 'Content Pipeline'] },
  { id: '3', name: 'Design', boards: ['Design System', 'Project Requests'] },
];

export function Sidebar({ collapsed, onToggleCollapse, onClose }: SidebarProps) {
  const location = useLocation();
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set(['1']));

  const toggleWorkspace = (workspaceId: string) => {
    setExpandedWorkspaces((prev) => {
      const next = new Set(prev);
      if (next.has(workspaceId)) {
        next.delete(workspaceId);
      } else {
        next.add(workspaceId);
      }
      return next;
    });
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="flex h-full flex-col bg-white/5 backdrop-blur-xl border-r border-white/10">
      {/* Sidebar header */}
      <div className="flex h-14 items-center justify-between px-4 border-b border-white/10">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <div className="relative h-8 w-8 rounded-lg flex items-center justify-center brass-shimmer overflow-hidden">
              <div
                className="absolute inset-0 animate-spin-gold"
                style={{
                  background: 'conic-gradient(from 0deg, #c9a633, #e8d48b, #c9a633, #8b7320, #c9a633)',
                }}
              />
              <div className="absolute inset-[1.5px] rounded-[calc(0.5rem-1.5px)] bg-[#0a0a0f]" />
              <span className="relative text-xl">📌</span>
            </div>
            <span className="font-semibold text-gray-100">Workspace</span>
          </Link>
        )}

        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-md hover:bg-white/10 text-gray-400"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Desktop collapse button */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex p-1.5 rounded-md hover:bg-white/10 text-gray-400"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-5 w-5" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation sections */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {/* Main navigation */}
        <ul className="space-y-1">
          {mainNavItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
                  transition-colors duration-150
                  ${isActive(item.href)
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-gray-300 hover:bg-white/10'
                  }
                  ${collapsed ? 'justify-center' : ''}
                `}
                title={collapsed ? item.name : undefined}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span>{item.name}</span>}
              </Link>
            </li>
          ))}
        </ul>

        {/* Divider */}
        <div className="my-4 border-t border-white/10" />

        {/* Workspaces section */}
        {!collapsed && (
          <div className="mb-4">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Workspaces
              </span>
              <button
                className="p-1 rounded hover:bg-white/10 text-gray-500"
                title="Add workspace"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>

            <ul className="space-y-1">
              {mockWorkspaces.map((workspace) => (
                <li key={workspace.id}>
                  <button
                    onClick={() => toggleWorkspace(workspace.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-white/10"
                  >
                    <div className="h-5 w-5 rounded bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-primary-400">
                        {workspace.name.charAt(0)}
                      </span>
                    </div>
                    <span className="flex-1 text-left truncate">{workspace.name}</span>
                    {expandedWorkspaces.has(workspace.id) ? (
                      <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                    )}
                  </button>

                  {/* Boards list */}
                  {expandedWorkspaces.has(workspace.id) && (
                    <ul className="ml-6 mt-1 space-y-1">
                      {workspace.boards.map((board) => (
                        <li key={board}>
                          <Link
                            to={`/boards/${workspace.id}/${board.toLowerCase().replace(/\s+/g, '-')}`}
                            className="block px-3 py-1.5 rounded-md text-sm text-gray-400 hover:bg-white/10 hover:text-gray-200"
                          >
                            {board}
                          </Link>
                        </li>
                      ))}
                      <li>
                        <button className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-gray-500 hover:bg-white/10 hover:text-gray-300">
                          <PlusIcon className="h-4 w-4" />
                          <span>Add board</span>
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Secondary navigation */}
        <div className={collapsed ? '' : 'mt-auto'}>
          <ul className="space-y-1">
            {secondaryNavItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
                    transition-colors duration-150
                    ${isActive(item.href)
                      ? 'bg-primary-500/15 text-primary-400'
                      : 'text-gray-300 hover:bg-white/10'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.name : undefined}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span>{item.name}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
